interface CassetteTapeProps {
  isPlaying: boolean;
  isRewinding?: boolean;
  isFastForwarding?: boolean;
}

const CassetteTape = ({
  isPlaying,
  isRewinding = false,
  isFastForwarding = false,
}: CassetteTapeProps) => {
  const getAnimationStyle = () => {
    if (isRewinding) {
      return {
        leftReel: "spin 1.5s linear infinite",
        rightReel: "spin 2s linear infinite",
        tapeMoving: "moveLeft 1s linear infinite",
      };
    } else if (isFastForwarding) {
      return {
        leftReel: "spin 1.5s linear infinite reverse",
        rightReel: "spin 2s linear infinite reverse",
        tapeMoving: "moveRight 1s linear infinite",
      };
    } else if (isPlaying) {
      return {
        leftReel: "spin 6s linear infinite reverse",
        rightReel: "spin 8s linear infinite reverse",
        tapeMoving: "moveRight 2s linear infinite",
      };
    } else {
      return {
        leftReel: "none",
        rightReel: "none",
        tapeMoving: "none",
      };
    }
  };

  const animationStyle = getAnimationStyle();

  return (
    <div>
      <style>{`
        @font-face {
          font-family: "BioRhyme";
          src: url(https://manzdev.github.io/twitch-cassette/assets/biorhyme.woff) format("woff");
        }
        
        @font-face {
          font-family: "Code";
          src: url(https://manzdev.github.io/twitch-cassette/assets/code.woff2) format("woff");
        }
        
        @font-face {
          font-family: "Desyrel";
          src: url(https://manzdev.github.io/twitch-cassette/assets/desyrel.woff) format("woff"),
               url(https://manzdev.github.io/twitch-cassette/assets/desyrel.woff2) format("woff2");
        }

        .tape-moving {
          width: 100%;
          height: 2px;
          background: linear-gradient(90deg, #8B4513 0%, #D2691E 50%, #8B4513 100%);
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          animation: ${animationStyle.tapeMoving};
        }

        @keyframes moveRight {
          0% { transform: translateX(-100%) translateY(-50%); }
          100% { transform: translateX(100%) translateY(-50%); }
        }

        @keyframes moveLeft {
          0% { transform: translateX(100%) translateY(-50%); }
          100% { transform: translateX(-100%) translateY(-50%); }
        }

        .cassette {
          width: var(--cassette-tape-shell-tape-width);
          background: #2f2f2f;
          background-image: linear-gradient(273deg, rgba(255,255,255,0.1), transparent),
                           repeating-linear-gradient(to bottom, #1c1c1c 3px, #2c2c2c 6px);
          border: 3px solid #404040;
          border-radius: 15px;
          display: flex;
          flex-direction: column;
          position: absolute;
          top: -35px;
          left: -50px; 
          box-shadow: 0 0 10px 2px rgba(34,39,34,0.4);
          margin: 0;
          transition: all 0.5s ease;
        }

        .cassette::before,
        .cassette::after {
          content: "";
          display: block;
          background: #333;
          width: 5px;
          height: 75px;
          position: absolute;
          bottom: 20px;
          clip-path: polygon(100% 0, 0 5%, 0 95%, 100% 100%);
        }

        .cassette::before {
          left: -8px;
        }

        .cassette::after {
          right: -8px;
          transform: scaleX(-1);
          background: #444;
        }

        .top-tape {
          display: flex;
          justify-content: space-between;
          height: 25px;
          padding: 0 25px;
        }

        .arrow-mark-container {
          background: rgba(0,0,0,0.25);
          display: flex;
        }

        .arrow-mark {
          color: #555;
          font-size: 32px;
          transform: scaleY(1.6) translate(0, -10px);
        }

        .brand-mark {
          background: rgba(0,0,0,0.25);
          font-family: "BioRhyme", serif;
          padding: 0 6px;
          color: #555;
        }

        .medium-tape {
          display: flex;
          justify-content: center;
        }

        .label-container {
          width: 400px;
          background: #1c1c1c;
          border-radius: 4px;
          padding: 4px;
        }

        .label {
          background: #eee;
          background-image: repeating-linear-gradient(transparent 0 20px, #c6c6c6 20px 21px),
                           linear-gradient(278deg, #eee, #ccc);
          border-radius: 10px;
        }

        .top-label {
          height: 62px;
          font-weight: normal;
          font-size: 16px;
          color: #333;
          position: relative;
        }

        .predefined-text {
          font-family: Arial;
          font-size: 9px;
          padding: 6px 6px 4px 10px;
          position: absolute;
        }

        .top-text {
          font-family: "pf-reminder", sans-serif;
          padding: 6px 10px;
          font-size: 24px;
          line-height: 110%;
          text-shadow: 0 0 1px #444;
          text-align: center;
        }

        .medium-label {
          height: 95px;
          background: #caa871;
          display: flex;
          justify-content: center;
          align-items: center;
          font-family: Arial, sans-serif;
        }

        .side-name {
          width: 50px;
          font-weight: bold;
          font-size: 28px;
          display: flex;
          justify-content: center;
          align-items: center;
          color: #222;
        }

        .gap-container {
          width: 360px;
          height: 75px;
          border: 4px solid #1c1c1c;
          background: #2f2f2f;
          background-image: linear-gradient(76deg, transparent, rgba(170,170,170,0.1));
          border-radius: 5px;
        }

        .gap {
          height: 100%;
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .tapereel {
          width: 50px;
          height: 50px;
          display: flex;
          justify-content: center;
          align-items: center;
          border-radius: 50%;
          border: 4px solid #aaa;
          box-shadow: 0 0 0 4px #111;
          background: #364147;
          position: relative;
        }

        .tapereel.left {
          animation: ${animationStyle.leftReel};
        }

        .tapereel.right {
          animation: ${animationStyle.rightReel};
        }

        .notch {
          background: linear-gradient(to right, #aaa 0 10%, transparent 12% 88%, #aaa 90% 100%);
          width: 50px;
          height: 8px;
          position: absolute;
        }

        .notch-1 { transform: rotate(60deg); }
        .notch-2 { transform: rotate(0deg); }
        .notch-3 { transform: rotate(-60deg); }

        .central-gap {
          width: 100px;
          height: 50px;
          border: 3px solid #222;
          background: #364147;
          margin: 0 10px;
          overflow: hidden;
          position: relative;
        }

        .central-gap::after {
          content: "";
          display: block;
          width: 100%;
          height: 100%;
          background-image: linear-gradient(transparent, rgba(255,255,255,0.3), transparent);
          position: absolute;
          top: 0;
        }

        .innertape {
          width: 150px;
          height: 150px;
          border-radius: 50%;
          background: radial-gradient(#999 45%, #3e3e3e 47% 100%);
          border: 2px solid #2e2e2e;
          position: relative;
        }

        .innertape.left {
          transform: translate(-119px, -52px);
        }

        .innertape.right {
          transform: translate(66px, -206px);
          box-shadow: -10px -10px 15px rgba(51,51,51,0.3);
        }

        .noise-reduction {
          width: 50px;
          font-size: 7px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          margin-left: 2px;
        }

        .box {
          display: flex;
          margin-top: 2px;
        }

        .box > div {
          border: 1px solid #444;
          background: #fff;
          font-size: 6px;
          padding: 0 3px;
          border-radius: 2px;
        }

        .bottom-label {
          border-bottom-left-radius: 10px;
          border-bottom-right-radius: 10px;
          background: #eee;
          background-image: linear-gradient(-278deg, #eee, #ccc);
          height: 32px;
          display: flex;
          justify-content: space-around;
          align-items: center;
        }

        .brand-text .maintext {
          font-family: "BioRhyme", sans-serif;
          font-size: 20px;
          color: #222;
          transform: translate(18px, 0) scaleX(1.4);
        }

        .brand-text .subtext {
          font-family: Arial, sans-serif;
          font-weight: bold;
          color: #333;
          font-size: 7px;
          transform: translateY(-7px);
        }

        .arrow-text {
          color: #4d7fb4;
          font-size: 32px;
          transform: scaleX(1.5) translate(-6px, -3px);
        }

        .model-text {
          font-family: "Code", sans-serif;
          font-weight: bold;
          color: #222;
          font-size: 28px;
          transform: translate(0, 2px);
        }

        .model-text span {
          color: #4d7fb4;
        }

        .bottom-tape {
          height: 80px;
          display: flex;
          justify-content: center;
          align-items: flex-end;
        }

        .magnetic-shield-container {
          filter: drop-shadow(-4px 0 1px #131313) drop-shadow(4px 0 1px #383838) drop-shadow(0 -4px 0 #202020);
        }

        .magnetic-shield {
          background: #2a2a2a;
          width: 350px;
          height: 65px;
          clip-path: polygon(0 100%, 4% 0, 96% 0, 100% 100%);
          display: flex;
          justify-content: space-between;
          background-image: repeating-linear-gradient(to bottom, #2c2c2c 3px, #1c1c1c 6px);
        }

        .left-hole-container,
        .right-hole-container {
          width: 125px;
          display: flex;
          justify-content: space-evenly;
          align-items: flex-end;
        }

        .hole.hole-2,
        .hole.hole-3 {
          transform: translateY(-15px);
          border: 3px solid #222;
          width: 24px;
          height: 18px;
          border-radius: 30px;
          box-shadow: 0 0 0 2px #111;
        }

        .hole.hole-3 {
          width: 20px;
          border-radius: 10px;
          transform-origin: 50% 50%;
          transform: rotate(90deg) translateX(-17px);
        }

        .screw-container::before {
          content: "";
          display: block;
          background: rgba(0, 0, 0, 0.75);
          width: 20px;
          height: 20px;
          border-radius: 50%;
          position: relative;
          top: 10px;
          z-index: 10;
        }

        .screw-container .screw {
          position: absolute;
          left: calc(50% - 8px);
          top: 10px;
          box-shadow: 0 0 2px 4px #000, 0 0 2px 8px #111;
        }

        .hole {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #111;
          border: 2px solid #191919;
          box-shadow: 0 0 0 2px #171717;
          transform: translateY(-5px);
        }

        .screw {
          background: linear-gradient(#555, #333);
          width: 16px;
          height: 16px;
          border-radius: 50%;
          position: absolute;
          display: flex;
          justify-content: center;
          align-items: center;
          border-top: 1px solid #222;
          border-bottom: 1px solid #666;
          transform: rotate(-25deg);
          box-shadow: 0 0 2px 2px #111;
        }

        .screw .dot {
          width: 4px;
          height: 4px;
          background: #222;
          border-radius: 50%;
          position: absolute;
          z-index: 5;
          filter: blur(1px);
        }

        .screw::before,
        .screw::after {
          content: " ";
          display: block;
          background: linear-gradient(to right, #222 10%, #111 45% 65%, #222 90%);
          width: 14px;
          height: 4px;
          border-radius: 2px;
          position: absolute;
        }

        .screw::before {
          transform: rotate(45deg);
        }

        .screw::after {
          transform: rotate(-45deg);
        }

        .screw.screw-1 {
          top: 4px;
          left: 4px;
        }

        .screw.screw-2 {
          top: 4px;
          right: 4px;
        }

        .screw.screw-3 {
          bottom: 4px;
          left: 4px;
        }

        .screw.screw-4 {
          bottom: 4px;
          right: 4px;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

      <div className="cassette">
        <div className="top-tape">
          <div className="arrow-mark-container">
            <div className="arrow-mark">🡁</div>
          </div>
          <div className="brand-mark">SONY</div>
        </div>
        <div className="medium-tape">
          <div className="label-container">
            <div className="label">
              <div className="top-label">
                <div className="predefined-text">INDEX</div>
                <div className="top-text">
                  <div>Werkschau 2025</div>
                  <div>Cassette Tape Story</div>
                </div>
              </div>
              <div className="medium-label">
                <div className="side-name">A</div>
                <div className="gap-container">
                  <div className="gap">
                    <div className="left tapereel">
                      <div className="notch notch-1"></div>
                      <div className="notch notch-2"></div>
                      <div className="notch notch-3"></div>
                    </div>
                    <div className="central-gap">
                      <div className="left innertape"></div>
                      <div className="right innertape"></div>
                    </div>
                    <div className="right tapereel">
                      <div className="notch notch-1"></div>
                      <div className="notch notch-2"></div>
                      <div className="notch notch-3"></div>
                    </div>
                  </div>
                </div>
                <div className="noise-reduction">
                  {/*NOISE REDUCTION*/}
                  <div className="box">
                    {/*<div>ON</div>
                    <div>OFF</div>*/}
                  </div>
                </div>
              </div>
              <div className="bottom-label">
                <div className="brand-text">
                  <div className="maintext">SONY</div>
                  <div className="subtext">TYPE I (NORMAL) POSITION</div>
                </div>
                <div className="arrow-text">
                  <span>🡆</span>
                </div>
                <div className="model-text">
                  <span>AHF</span>46
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="bottom-tape">
          <div className="magnetic-shield-container">
            <div className="magnetic-shield">
              <div className="left-hole-container">
                <div className="hole hole-1"></div>
                <div className="hole hole-2"></div>
              </div>
              <div className="screw-container">
                <div className="screw"></div>
              </div>
              <div className="right-hole-container">
                <div className="hole hole-3"></div>
                <div className="hole hole-4"></div>
              </div>
            </div>
          </div>
        </div>
        <div className="screw screw-1">
          <div className="dot"></div>
        </div>
        <div className="screw screw-2">
          <div className="dot"></div>
        </div>
        <div className="screw screw-3">
          <div className="dot"></div>
        </div>
        <div className="screw screw-4">
          <div className="dot"></div>
        </div>
      </div>
    </div>
  );
};

export default CassetteTape;
